import { useEffect, useState } from 'react';
import type { AffiliatedHolder } from '../types';
import type { OrgChartPersonMatch } from '../lib/db';
import { SERVES_OUTSIDE_LABEL, SERVES_OUTSIDE_TITLE, assignErrorMessage, type OutsideAssignState } from '../lib/affiliatedHolders';

/** Who holds this seat from outside the district, with (for administrators) a way to end it. They have no roster record in this
 *  district, so there is no roster entry to edit: the seat is ended where it is held. Other people see them, read-only. */
export function OutsideDistrictHolders({
  holders, isAdmin, onRemove,
}: {
  holders: AffiliatedHolder[];
  isAdmin: boolean;
  /** Ends this person's hold on the seat. Throws with a readable message on failure. */
  onRemove: (holder: AffiliatedHolder) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (holders.length === 0) return null;

  const remove = async (h: AffiliatedHolder) => {
    setBusy(h.assignmentId); setErr(null);
    try { await onRemove(h); setConfirming(null); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove.'); } finally { setBusy(null); }
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 6 }}>Holds this seat from outside the district</div>
      {holders.map((h) => (
        <div key={h.assignmentId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
          <div>
            <b>{h.name}</b> <span className="org-tag" title={SERVES_OUTSIDE_TITLE}>{SERVES_OUTSIDE_LABEL}</span>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              {isAdmin ? 'Their registration and roster record are not changed by this seat.' : 'Managed by a council administrator.'}
            </div>
          </div>
          {isAdmin && (confirming === h.assignmentId ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn" disabled={busy === h.assignmentId} onClick={() => void remove(h)}>{busy === h.assignmentId ? 'Removing…' : `Remove ${h.name}`}</button>
              <button className="btn" onClick={() => setConfirming(null)}>Keep</button>
            </div>
          ) : (
            <button className="btn" onClick={() => setConfirming(h.assignmentId)}>Remove</button>
          ))}
        </div>
      ))}
      {err && <div className="err" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
}

/** Administrator-only: assign someone who is registered elsewhere in the council (another district, or at council level) to a seat in
 *  THIS district. Nothing is created or edited besides the seat itself — no roster record, no sign-in, no permission, no change to
 *  their my.Scouting registration — and the screen says so before it is confirmed. Only a seat that is open (or takes several
 *  holders) is offered; replacing an occupied seat is not available here. */
export function OutsideDistrictAssign({
  state, positionName, districtName, search, onAssign,
}: {
  state: OutsideAssignState;
  positionName: string;
  districtName: string;
  /** Council-wide search of People (administrators only on the server). */
  search: (query: string) => Promise<OrgChartPersonMatch[]>;
  /** Assigns the person to this seat. Resolves with whether it was recorded as an outside-district assignment. */
  onAssign: (person: OrgChartPersonMatch) => Promise<{ affiliated: boolean }>;
}) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<OrgChartPersonMatch[]>([]);
  const [searched, setSearched] = useState(false);
  const [picked, setPicked] = useState<OrgChartPersonMatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (state !== 'open' || !q) { setMatches([]); setSearched(false); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      search(q)
        .then((people) => { if (!cancelled) { setMatches(people.slice(0, 8)); setSearched(true); } })
        .catch((e: unknown) => { if (!cancelled) { setMatches([]); setSearched(true); setErr(assignErrorMessage(e, 'Search failed.')); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, state, search]);

  if (state === 'hidden') return null;
  if (state === 'held') {
    return (
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
        This seat is held, so no one can be assigned to it from outside the district. To change who holds it, remove the current holder first.
      </div>
    );
  }

  const confirm = async () => {
    if (!picked) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const result = await onAssign(picked);
      setNote(result.affiliated
        ? `${picked.displayName} now holds ${positionName} from outside the district.`
        : `${picked.displayName} is on this district's roster, so ${positionName} was added to their roster record.`);
      setPicked(null); setQuery(''); setMatches([]);
    } catch (e) {
      setErr(assignErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 2 }}>Assign someone from outside this district</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
        For a person registered in another district or at the council level who serves in this one. Search everyone in the council.
      </div>
      {!picked && (
        <div style={{ position: 'relative' }}>
          <input aria-label="Search the council" placeholder="Search the whole council by name or email…" value={query} onChange={(e) => { setQuery(e.target.value); setNote(null); setErr(null); }} />
          {query.trim() && searched && (
            <div className="people-dropdown">
              {matches.map((p) => (
                <button key={p.personId} type="button" className="people-option" disabled={p.status === 'provisional'} onClick={() => setPicked(p)}>
                  {p.displayName}
                  {p.status === 'provisional' ? ' (identity not confirmed yet — cannot be assigned)' : ''}
                  {p.evidence.length > 0 ? ` — ${p.evidence.slice(0, 2).join(', ')}` : ''}
                </button>
              ))}
              {matches.length === 0 && <div className="people-option-empty">No matches.</div>}
            </div>
          )}
        </div>
      )}
      {picked && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontWeight: 650, marginBottom: 6 }}>Assign {picked.displayName} to {positionName} in {districtName}?</div>
          <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 13 }}>
            <li>They will be shown as “{SERVES_OUTSIDE_LABEL}”.</li>
            <li>Their official my.Scouting registration and any roster record they have are not changed, and no new roster record is created.</li>
            <li>This does not give them sign-in access or change any permission.</li>
          </ul>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn primary" disabled={busy} onClick={() => void confirm()}>{busy ? 'Assigning…' : 'Assign'}</button>
            <button className="btn" disabled={busy} onClick={() => setPicked(null)}>Cancel</button>
          </div>
        </div>
      )}
      {note && <div style={{ fontSize: 13, marginTop: 8 }}>{note}</div>}
      {err && <div className="err" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
}
